type FieldValueAtomic = string | number | undefined;

export type FieldValue = FieldValueAtomic | FieldValueObject[] | FieldValueObject;

export interface FieldValueObject {
	[name: string]: FieldValue;
}

function retrieveParent(parent: FieldValueObject, path: string[], index: number): FieldValueObject {
	if (index >= path.length - 1) {
		return parent;
	}
	const nextParent = <FieldValueObject | undefined> parent[path[index]];
	if (nextParent !== undefined) {
		return retrieveParent(nextParent, path, index + 1);
	}
	const newParent: FieldValueObject = {};
	parent[path[index]] = newParent;
	return retrieveParent(newParent, path, index + 1);
}

export function isFieldValueObject(value: FieldValue) {
	if (value === undefined) {
		return false;
	}
	const type = typeof value;
	return type !== 'number' && type !== 'string' && !Array.isArray(value);
}

export function setFieldValue(fieldName: string, fieldValue: FieldValue, dest: FieldValueObject) {
	dest[fieldName] = fieldValue;
	return dest;
}

export function addFieldValue(fieldPath: string, fieldValue: FieldValue, dest: FieldValueObject) {
	const pathSteps = fieldPath.split('/');
	const parent = retrieveParent(dest, pathSteps, 0);
	parent[pathSteps[pathSteps.length - 1]] = fieldValue;
	return dest;
}

export type FieldType = 'objectCst'
	| 'listCst' | 'listVar'
	| 'textCst' | 'textVar' | 'textSelection'
	| 'numberCst' | 'numberVar';

export abstract class Field<T extends FieldValue> {
	constructor(
		readonly isVar: boolean,
		readonly type: FieldType,
		readonly name: string
	) {}

	abstract value(): T | undefined;

	abstract collectVarValue(object: FieldValueObject): void;

	abstract setValue(value: FieldValue): void;
}

export abstract class FieldAtomic<T extends FieldValueAtomic> extends Field<T> {
	readonly originalValue: T | undefined;
	constructor(
		isVar: boolean,
		type: FieldType,
		name: string,
		originalValue: T | undefined
	) {
		super(isVar, type, name);
		this.originalValue = originalValue;
	}

	isOriginalValueDifferent() {
		const { originalValue } = this;
		const value = this.value();
		// it is required to check this way because value could be a number
		// -> 0 === undefined => true, and false is expected instead
		if (originalValue === undefined) {
			return value !== undefined;
		}
		if (value === undefined) {
			return true;
		}
		return originalValue !== value;
	}
}
